import { Button, Card, Flex, Text } from '@radix-ui/themes';
import { DeliveryControl } from 'components/common/delivery-control';
import { CommonDialog } from 'components/common/dialogs';
import { VideoSelectionForm } from 'components/common/dialogs/video-selection/form';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import { CommonVideoPreview } from 'components/common/video/preview';
import { t } from 'i18next';
import { VideoChangedFn } from 'interfaces/function-types';
import { DEFAULT_ACCEPT_BTN, IBaseDialog } from 'interfaces/i-dialogs';
import { IDeliveryProps } from 'interfaces/i-video-options';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { useMemo, useState } from 'react';
import { useVideosStore } from 'stores/use-videos-store';

/** shared by the dialog and form components */
export interface IVideoSelectionProps {
  /** provide for default value */
  videoID: Nullable<string>;

  /** for passing the selected video_id value back to parent component */
  handleChange: VideoChangedFn;
}

interface IProps extends IBaseDialog, IVideoSelectionProps, IDeliveryProps {
  // when false, only the button to trigger onOpen is shown
  showDialog?: boolean;
  showCurrent?: boolean;

  /** for parent to show dialog */
  onOpen?: () => void;

  /** from release position */
  px: number;
}

/**
 * video_id selections in the embedded form will only update this component's state video_id
 * iff accepted for close, this will pass its state video_id to the parent
 */
export const VideoSelectionDialog = (props: IProps) => {
  const [videoID, setVideoID] = useState(props.videoID);

  const options = useMemo(() => {
    const result = useVideosStore.getState().getVideoOptions({
      releasePx: props.px,
    });

    return result.options;
  }, [props.px]);

  const selected = useMemo(
    () =>
      props.videoID
        ? options.find((vo) => vo.value === props.videoID)
        : undefined,
    [options, props.videoID]
  );

  return (
    <ErrorBoundary identifier="VideoSelectionDialog">
      {(props.showCurrent || props.onOpen) && (
        <CommonInputWrapper id="video-button">
          {props.showCurrent && (
            <CommonInputLabel
              label={
                selected?.video.VideoTitle ||
                selected?.video.VideoFileName ||
                'videos.no-video'
              }
            />
          )}

          {props.onOpen && (
            <div>
              <Button onClick={props.onOpen}>{t('videos.select-video')}</Button>
            </div>
          )}
        </CommonInputWrapper>
      )}

      {props.showDialog && (
        <CommonDialog
          identifier={props.identifier}
          width={RADIX.DIALOG.WIDTH.MD}
          title="videos.assign-video"
          content={
            <CommonFormGrid columns={1}>
              <DeliveryControl
                delivery={props.delivery}
                onChangeDelivery={props.onChangeDelivery}
              />

              <VideoSelectionForm
                videoID={props.videoID}
                options={options}
                handleChange={(v) => {
                  setVideoID(v);
                }}
              />

              {videoID ? (
                <CommonVideoPreview
                  identifier="VideoSelectionVideoPreview"
                  videoID={videoID}
                  delivery={props.delivery}
                  onChangeDelivery={props.onChangeDelivery}
                  hideDeliveryControl
                  hideHeader
                />
              ) : (
                <Card
                  style={{
                    aspectRatio: '16 / 10',
                    textAlign: 'center',
                  }}
                >
                  <Flex height="100%" align="center" justify="center">
                    <Text color={RADIX.COLOR.NEUTRAL}>
                      {t('videos.no-video-selected')}
                    </Text>
                  </Flex>
                </Card>
              )}
            </CommonFormGrid>
          }
          buttons={[
            {
              ...DEFAULT_ACCEPT_BTN,
              onClick: () => {
                props.handleChange(videoID);
                props.onClose();
              },
            },
          ]}
          onClose={props.onClose}
        />
      )}
    </ErrorBoundary>
  );
};
